import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import {
  createCampaign,
  fetchCampaigns,
  fetchSegments,
  sendCampaignSandbox,
} from '@/data/sources-marketinga';
import type { Campaign, CampaignStatus, SandboxSendResult, Segment } from '@/types/marketinga';
import { formatRelative } from '@/lib/utils';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

/* /marketing/campaigns — Campaigns (W3b). List + create + a SANDBOX send.
 *
 * HONESTY + SAFETY INVARIANTS (W3b contract):
 *  - SANDBOX ONLY: the only send here is POST /marketing/campaigns/{id}/
 *    send-sandbox — allowlist + suppression enforced, DRY EMAIL_SENT events,
 *    NO real email ever leaves the system. The UI states this loudly and the
 *    result restates it; there is deliberately no real-send control.
 *  - The list is a FREE read (auto-loads). Create + send-sandbox are explicit
 *    writes, owner/operator only.
 *  - Source-badged; a stage/metric the backend hasn't produced stays honest
 *    (audience "Not counted yet", never a fabricated 0).
 */

const SOURCE_TAG = 'Source: Campaigns (bc_campaigns)';

const STATUS_TONE: Record<CampaignStatus, Parameters<typeof Badge>[0]['tone']> = {
  draft: 'neutral',
  scheduled: 'compliance',
  sending: 'ai',
  sent: 'success',
  paused: 'warning',
};

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** Audience cell — honest null (never counted) is explicit, never a fake 0. */
function audienceLabel(n: number | null): string {
  return n === null ? 'Not counted yet' : formatInt(n);
}

function scheduleLabel(iso: string | null): string {
  if (!iso) return 'Unscheduled (draft)';
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? iso : new Date(iso).toLocaleString('en-GB');
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Campaigns are limited to workspace owners and operators.';
    if (err.status === 422) return err.message || 'Please check the campaign fields and try again.';
    return err.message || 'The data service returned an unexpected response. Nothing was sent.';
  }
  return err instanceof Error ? err.message : 'Unexpected error — nothing was sent. Try again.';
}

const inputClass =
  'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

/* -------------------------------------------------------------------------- */
/* SANDBOX banner — reused so the "no real email" guarantee is unmissable.     */
function SandboxNotice() {
  return (
    <div
      role="note"
      className="rounded-md border border-compliance/30 bg-compliance/10 px-3 py-2 text-sm text-text-primary"
    >
      <span className="font-semibold">SANDBOX — no real email is sent.</span>{' '}
      <span className="text-text-secondary">
        The send action simulates delivery through the sandbox worker: the audience is claimed, the
        allowlist and suppression list are enforced, and dry send events are recorded. No message
        ever reaches a real inbox.
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Create form — explicit write, owner/operator only.                          */
function CreateCampaignForm({
  segments,
  disabled,
  onCreated,
}: {
  segments: Segment[];
  disabled: boolean;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [schedule, setSchedule] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    !disabled && !busy && name.trim() !== '' && templateId.trim() !== '' && segmentId !== '';

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // Local datetime → ISO UTC; blank stays an unscheduled draft (null).
      const iso = schedule.trim() === '' ? null : new Date(schedule).toISOString();
      await createCampaign({
        name: name.trim(),
        template_id: templateId.trim(),
        segment_id: segmentId,
        schedule: iso,
      });
      setName('');
      setTemplateId('');
      setSegmentId('');
      setSchedule('');
      onCreated();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="engine">New campaign</Badge>
        <span className="text-sm font-medium text-text-primary">Create a campaign</span>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        A campaign renders a template to an audience segment. Creating it never sends — sending is a
        separate, sandbox-only action below.
      </p>

      {disabled && (
        <div role="status" className="mt-3 rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Creating campaigns is limited to workspace owners and operators.
        </div>
      )}

      <form
        className="mt-3 grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Campaign name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wales pilates — Q3 intro"
            aria-label="Campaign name"
            className={inputClass}
            disabled={disabled || busy}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Template ID</span>
          <input
            type="text"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="e.g. tpl_intro_v3"
            aria-label="Template ID"
            className={inputClass}
            disabled={disabled || busy}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Audience segment</span>
          <select
            value={segmentId}
            onChange={(e) => setSegmentId(e.target.value)}
            aria-label="Audience segment"
            className={inputClass}
            disabled={disabled || busy || segments.length === 0}
          >
            <option value="">
              {segments.length === 0 ? 'No segments yet — create one first' : 'Choose a segment…'}
            </option>
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.count !== null ? ` (${formatInt(s.count)})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">Schedule (optional)</span>
          <input
            type="datetime-local"
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
            aria-label="Schedule send time"
            className={inputClass}
            disabled={disabled || busy}
          />
        </label>

        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create campaign'}
          </Button>
          {error && <span className="text-sm text-danger">{error}</span>}
        </div>
      </form>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Sandbox send result panel.                                                  */
function SandboxResult({ result }: { result: SandboxSendResult }) {
  const cells: Array<{ label: string; value: number; tone: Parameters<typeof Badge>[0]['tone'] }> = [
    { label: 'Dispatched (dry)', value: result.dispatched, tone: 'success' },
    { label: 'Skipped — suppressed', value: result.skipped_suppressed, tone: 'warning' },
    { label: 'Blocked — not allowlisted', value: result.blocked_not_allowlisted, tone: 'danger' },
  ];
  // D-3 honest disclosure — present only when the backend actually sent them.
  // `null` narrowing keeps the branch honest: we never invent an audience size,
  // and we NEVER imply the whole audience ran unless the backend confirms it.
  const audienceTotal = result.audience_total ?? null;
  const truncated = result.truncated === true;
  return (
    <div className="mt-3 rounded-md border border-border-subtle bg-surface p-3">
      {audienceTotal !== null && (
        <div className="mb-3 rounded-md border border-border-subtle bg-canvas px-3 py-2 text-sm">
          <span className="text-text-secondary">True audience size: </span>
          <span className="font-mono font-semibold text-text-primary" data-tabular>
            {formatInt(audienceTotal)}
          </span>
          <span className="text-text-secondary">
            {truncated
              ? ' recipients'
              : ' recipients — the full audience was covered by this simulation.'}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {cells.map((c) => (
          <div key={c.label} className="rounded-lg border border-border-subtle bg-canvas p-3 text-center">
            <div className="font-mono text-2xl font-semibold text-text-primary" data-tabular>
              {formatInt(c.value)}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-wide text-text-tertiary">{c.label}</div>
          </div>
        ))}
      </div>

      {truncated && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-text-primary"
        >
          <span className="font-semibold">
            Sandbox preview limited to the first {formatInt(result.dispatched)} recipients.
          </span>{' '}
          <span className="text-text-secondary">
            {audienceTotal !== null
              ? `Your full audience is ${formatInt(audienceTotal)} recipients — the rest were not previewed here. `
              : 'Not every recipient was previewed here. '}
            This simulation did not process the whole audience; the complete audience will be
            included when live-send is enabled.
          </span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-text-tertiary">
        <Badge tone="compliance">Source: {result.source}</Badge>
        <span>
          Simulated only — these are dry send events from the sandbox worker. No real email was
          sent; the suppression list and allowlist were enforced before any would-be send.
        </span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Aura draft review (Lane 3C) — a newsletter DRAFT prepared by Aura's intake.  */
/* Read-only preview + an explicit owner approval. SENDING STAYS DISABLED: the  */
/* connector posture is gated and there is deliberately no send control here.   */
interface AuraPreview {
  subject: string;
  body: string;
  cta: string;
  link: string;
  timing: string;
  approved: boolean;
  scope?: { level?: string };
  audience?: { segment_name?: string; recipient_count?: number | null };
  send?: { status?: string; send?: boolean; note?: string };
}
interface AuraDraftDetail {
  campaign?: { id?: string | number; name?: string; status?: string };
  preview?: AuraPreview | null;
  connectors?: { status?: string; send?: boolean; note?: string } | null;
}

function GatedSendNotice({ note }: { note?: string }) {
  return (
    <div
      role="note"
      className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-text-primary"
    >
      <span className="font-semibold">Sending is disabled.</span>{' '}
      <span className="text-text-secondary">
        {note ??
          'No email connector is enabled. Aura prepares a draft only — nothing is scheduled or sent.'}
      </span>
    </div>
  );
}

function AuraDraftReview({
  campaignId,
  canManage,
  onApproved,
}: {
  campaignId: string;
  canManage: boolean;
  onApproved: () => void;
}) {
  const queryClient = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['marketing', 'campaign-detail', campaignId],
    queryFn: () => api.get<AuraDraftDetail>('/marketing/campaigns/' + campaignId),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = detailQ.data?.preview ?? null;
  const connectors = detailQ.data?.connectors ?? null;

  const approve = async () => {
    if (!canManage || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/marketing/campaigns', {
        source: 'aura_action',
        intent: 'approve',
        campaign_id: Number(campaignId),
      });
      await queryClient.invalidateQueries({ queryKey: ['marketing', 'campaign-detail', campaignId] });
      onApproved();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  if (detailQ.isPending) return <LoadingState rows={3} />;
  if (detailQ.error) return <ErrorState error={detailQ.error} onRetry={() => void detailQ.refetch()} />;
  if (!preview) {
    return (
      <div className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
        This campaign has no Aura brief to review. It was created directly rather than prepared by
        Aura.
      </div>
    );
  }

  const recipients = preview.audience?.recipient_count ?? null;

  return (
    <div className="space-y-3 rounded-md border border-border-subtle bg-surface p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="ai">Aura draft</Badge>
        {preview.approved ? (
          <Badge tone="success">Approved — awaiting connector</Badge>
        ) : (
          <Badge tone="warning">Awaiting your approval</Badge>
        )}
        <Badge tone="neutral">
          Scope: {preview.scope?.level === 'workspace' ? 'Whole workspace' : preview.scope?.level ?? 'workspace'}
        </Badge>
      </div>

      <GatedSendNotice note={connectors?.note ?? preview.send?.note} />

      <div>
        <div className="text-xs font-medium text-text-secondary">Subject</div>
        <div className="text-sm text-text-primary">
          {preview.subject || <span className="italic text-text-tertiary">Not set</span>}
        </div>
      </div>
      <div>
        <div className="text-xs font-medium text-text-secondary">Message preview</div>
        <div className="whitespace-pre-wrap rounded-md border border-border-subtle bg-canvas px-3 py-2 text-sm text-text-primary">
          {preview.body || <span className="italic text-text-tertiary">No message content yet</span>}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <div className="text-xs font-medium text-text-secondary">Audience</div>
          <div className="text-sm text-text-primary">
            {preview.audience?.segment_name || '—'}
            {recipients !== null && (
              <span className="text-text-tertiary"> ({formatInt(recipients)} recipients)</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-text-secondary">Call to action</div>
          <div className="text-sm text-text-primary">{preview.cta || '—'}</div>
        </div>
        <div>
          <div className="text-xs font-medium text-text-secondary">Timing</div>
          <div className="text-sm text-text-primary">{preview.timing || '—'}</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          onClick={() => void approve()}
          disabled={!canManage || busy || preview.approved}
          title={canManage ? 'Approve this draft — no email is sent' : 'Owner/operator only'}
        >
          {preview.approved ? 'Approved' : busy ? 'Approving…' : 'Approve draft'}
        </Button>
        <span className="text-xs text-text-tertiary">
          Approving records your sign-off. It never sends — delivery stays disabled until the email
          connector is enabled.
        </span>
      </div>
      {error && <div className="text-sm text-danger">{error}</div>}
    </div>
  );
}

/* ========================================================================== */
export function CampaignsPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const queryClient = useQueryClient();

  // FREE read-only surface — auto-loads. No send on mount.
  const campaignsQ = useQuery({ queryKey: ['marketing', 'campaigns'], queryFn: fetchCampaigns });
  // Free segment list feeds the create form's audience picker.
  const segmentsQ = useQuery({ queryKey: ['marketing', 'segments'], queryFn: fetchSegments });

  const campaigns = campaignsQ.data?.campaigns ?? [];
  const segments = segmentsQ.data?.segments ?? [];
  const source = campaignsQ.data?.source ?? 'bc_campaigns';

  // Sandbox-send state, keyed per campaign so the row that ran is the one shown.
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<{ id: string; result: SandboxSendResult } | null>(null);
  const [sendError, setSendError] = useState<{ id: string; message: string } | null>(null);
  // Aura draft review panel (Lane 3C) — which campaign's brief is open for review.
  const [reviewId, setReviewId] = useState<string | null>(null);

  const runSandbox = async (campaign: Campaign) => {
    if (!canManage || sendingId !== null) return;
    setSendingId(campaign.id);
    setSendError(null);
    setSendResult(null);
    try {
      const result = await sendCampaignSandbox(campaign.id);
      setSendResult({ id: campaign.id, result });
    } catch (err) {
      setSendError({ id: campaign.id, message: describeError(err) });
    } finally {
      setSendingId(null);
    }
  };

  const columns: SortableColumn<Campaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      sortable: true,
      sortValue: (c) => c.name,
      render: (c) => (
        <div>
          <div className="text-sm font-medium text-text-primary">{c.name}</div>
          <div className="text-xs text-text-tertiary">
            {c.template_id ? `Template ${c.template_id}` : 'No template'}
          </div>
        </div>
      ),
      cellClassName: 'text-text-primary',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: (c) => c.status,
      render: (c) => <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>,
    },
    {
      key: 'audience',
      header: 'Audience',
      align: 'right',
      numeric: true,
      sortable: true,
      // Null (never counted) sorts LAST via the table's null-honest rule.
      sortValue: (c) => c.audience_count,
      render: (c) =>
        c.audience_count === null ? (
          <span className="text-xs italic text-text-tertiary">Not counted yet</span>
        ) : (
          <span className="font-mono text-sm text-text-primary" data-tabular>
            {audienceLabel(c.audience_count)}
          </span>
        ),
    },
    {
      key: 'schedule',
      header: 'Schedule',
      render: (c) => <span className="text-sm text-text-secondary">{scheduleLabel(c.schedule)}</span>,
    },
    {
      key: 'created',
      header: 'Created',
      align: 'right',
      render: (c) => (
        <span className="text-xs text-text-tertiary" data-tabular>
          {c.created_at ? formatRelative(c.created_at) : '—'}
        </span>
      ),
    },
    {
      key: 'send',
      header: 'Sandbox send',
      align: 'right',
      render: (c) => (
        <Button
          variant="secondary"
          onClick={() => void runSandbox(c)}
          disabled={!canManage || sendingId !== null}
          title={canManage ? 'Simulate a send — no real email' : 'Owner/operator only'}
        >
          {sendingId === c.id ? 'Simulating…' : 'Send (sandbox)'}
        </Button>
      ),
    },
    {
      key: 'review',
      header: 'Aura draft',
      align: 'right',
      render: (c) => (
        <Button
          variant="ghost"
          onClick={() => setReviewId((cur) => (cur === c.id ? null : c.id))}
          title="Review the Aura brief and approve this draft — no email is sent"
        >
          {reviewId === c.id ? 'Hide brief' : 'Review & approve'}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaigns"
        subtitle="Create campaigns and simulate delivery safely. Sending is sandbox-only — the audience is claimed and suppression/allowlist enforced, but no real email is ever sent."
      />

      <SandboxNotice />

      <CreateCampaignForm
        segments={segments}
        disabled={!canManage}
        onCreated={() => void queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] })}
      />

      {campaignsQ.isPending ? (
        <LoadingState rows={5} />
      ) : campaignsQ.error ? (
        <ErrorState error={campaignsQ.error} onRetry={() => void campaignsQ.refetch()} />
      ) : (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{SOURCE_TAG}</Badge>
            <span className="text-xs text-text-tertiary">
              {campaigns.length
                ? `${formatInt(campaigns.length)} ${campaigns.length === 1 ? 'campaign' : 'campaigns'}`
                : 'No campaigns yet'}
            </span>
          </div>

          <div className="mt-4">
            {campaigns.length === 0 ? (
              <EmptyState
                title="No campaigns yet"
                teach="Create your first campaign above — pick a template and an audience segment. Nothing sends automatically; when you're ready you can simulate delivery through the sandbox, which never sends a real email."
              />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <SortableTable
                  rows={campaigns}
                  columns={columns}
                  getRowKey={(c) => c.id}
                  minWidthClass="min-w-[860px]"
                  caption="Campaigns with status, audience, schedule and a sandbox send action"
                />
                <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                  {formatInt(campaigns.length)} {campaigns.length === 1 ? 'campaign' : 'campaigns'} ·
                  read from {source} · the only send action here is a sandbox simulation — no real
                  email is ever sent.
                </div>
              </div>
            )}
          </div>

          {/* Per-campaign sandbox outcome, shown under the table. */}
          {sendError && (
            <div role="alert" className="mt-3 rounded-md border border-warning/30 bg-surface px-3 py-2 text-sm text-text-secondary">
              Sandbox simulation for{' '}
              <span className="font-medium text-text-primary">
                {campaigns.find((c) => c.id === sendError.id)?.name ?? 'campaign'}
              </span>{' '}
              could not run: {sendError.message}
            </div>
          )}
          {sendResult && (
            <div className="mt-3">
              <div className="text-sm font-medium text-text-primary">
                Sandbox simulation ·{' '}
                {campaigns.find((c) => c.id === sendResult.id)?.name ?? 'campaign'}
              </div>
              <SandboxResult result={sendResult.result} />
            </div>
          )}

          {reviewId && (
            <div className="mt-4">
              <div className="mb-2 text-sm font-medium text-text-primary">
                Draft review · {campaigns.find((c) => c.id === reviewId)?.name ?? 'campaign'}
              </div>
              <AuraDraftReview
                campaignId={reviewId}
                canManage={canManage}
                onApproved={() =>
                  void queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] })
                }
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
